import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '@store/redux.store'
import { taskTrackerApi } from '../../api/taskTracker.api'
import { getErrorMessage } from '../shared/thunk.utils'
import type { TaskTrackerState } from '../index'
import { selectTaskTrackerProjectMembers } from './projectMembers.selectors'

const pendingProjectMembersRequests = new Set<number>()

type AppState = RootState & {
  taskTracker: TaskTrackerState
}

export type FetchProjectMembersArg =
  | number
  | {
      projectId: number
      force?: boolean
    }

export const resolveFetchProjectMembersArg = (arg: FetchProjectMembersArg) =>
  typeof arg === 'number'
    ? { projectId: arg, force: false as const }
    : { projectId: arg.projectId, force: arg.force ?? false }

export const fetchProjectMembersThunk = createAsyncThunk(
  'taskTracker/fetchProjectMembers',
  async (arg: FetchProjectMembersArg, { rejectWithValue }) => {
    const { projectId } = resolveFetchProjectMembersArg(arg)

    try {
      const project = await taskTrackerApi.getProject(projectId)
      return {
        projectId,
        members: project.members,
        roles: project.roles,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        projectId,
        error: getErrorMessage(error, 'Не удалось загрузить участников проекта'),
      })
    } finally {
      pendingProjectMembersRequests.delete(projectId)
    }
  },
  {
    condition: (arg, { getState }) => {
      const { projectId, force } = resolveFetchProjectMembersArg(arg)

      if (pendingProjectMembersRequests.has(projectId)) {
        return false
      }

      if (!force) {
        const existing = selectTaskTrackerProjectMembers(
          getState() as AppState,
          projectId,
        )
        if (existing.members.length > 0) {
          return false
        }
      }

      pendingProjectMembersRequests.add(projectId)
      return true
    },
  },
)
